export function HelpPage() {
  return (
    <div className="min-h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl dark:text-slate-100">Help</h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          How to use SOPerator AI chat, context, and knowledge-base search together.
        </p>

        <section className="mt-10 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Chat</h2>
          <p className="leading-relaxed text-slate-700 dark:text-slate-300">
            Open <strong>Chat</strong> from the header to talk to the assistant. Each conversation is a
            separate thread; use the sidebar to switch chats, start a new one, rename, or archive-style
            organization as you prefer.
          </p>
          <ul className="list-disc space-y-2 pl-5 leading-relaxed text-slate-700 dark:text-slate-300">
            <li>
              Type your question in the prompt area and choose <strong>Send Message</strong>. The assistant
              replies in the main panel.
            </li>
            <li>
              Replies may include citations from your <strong>Knowledge Base</strong> when relevant passages
              are found (you can open source details on supported assistant messages).
            </li>
            <li>
              Use the <strong>Context &amp; Prompt</strong> column to adjust what the model knows about your
              situation before you send—not only which documents to search, but also jurisdiction, state,
              and optional organization context.
            </li>
          </ul>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">What context is for</h2>
          <p className="leading-relaxed text-slate-700 dark:text-slate-300">
            Context is the extra information you attach to a chat so answers match your scenario. It does
            two different jobs:
          </p>
          <ul className="list-disc space-y-2 pl-5 leading-relaxed text-slate-700 dark:text-slate-300">
            <li>
              <strong>Document scope (RAG):</strong> If you <em>do not</em> pick specific knowledge-base
              files, the assistant can search across <em>all</em> indexed knowledge-base documents for snippets
              related to your latest question. If you <em>do</em> add specific documents in context, search
              is limited to those files only—useful when you know exactly which SOPs or policies apply.
            </li>
            <li>
              <strong>Guidance fields:</strong> Jurisdiction, state or region, tags, and optional{' '}
              <strong>organization</strong> (name / EIN in the demo) are passed to the model so it frames
              answers correctly—for example, “this question is about a nonprofit client in Oregon” or “this
              thread is scoped to Organization X.” They do not replace document text; they steer how retrieved
              excerpts and general guidance are combined.
            </li>
          </ul>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Searching all documents vs narrowing first
          </h2>
          <p className="leading-relaxed text-slate-700 dark:text-slate-300">
            You can use the product in two complementary ways:
          </p>
          <ul className="list-disc space-y-2 pl-5 leading-relaxed text-slate-700 dark:text-slate-300">
            <li>
              <strong>Broad search:</strong> Leave the document list in context empty. Ask naturally; the
              system will look across every indexed knowledge-base document and pull in the most relevant
              chunks for that turn.
            </li>
            <li>
              <strong>Narrow then dig in:</strong> Use the Knowledge Base to find the right materials, add
              those files to chat context, then ask detailed questions. That keeps answers tightly tied to
              the policies you selected. You can still add organization or location context so the assistant
              treats the user or entity correctly while sticking to those documents.
            </li>
          </ul>
          <p className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm leading-relaxed text-slate-700 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-300">
            Tip: Organization and profile-style context in the demo is sample data for UI review. In a full
            deployment, the same pattern can tie chats to real CRM or client records while document search
            stays under your control via context.
          </p>
        </section>

        <section className="mt-10 space-y-4 border-t border-slate-200 pt-10 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Knowledge Base</h2>
          <p className="leading-relaxed text-slate-700 dark:text-slate-300">
            The <strong>Knowledge Base</strong> page is where markdown documents are listed, tagged, and
            indexed. Documents must be indexed to appear in chat search. From there you can open a file or
            add it into a chat&apos;s context for scoped questions.
          </p>
        </section>
      </div>
    </div>
  );
}
